/* BadgeMovementsCrudCard.tsx
    Une card affichant la gestion des mouvements de badges
    Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
    Utilise l'API correspondante pour les opérations de données
*/

import React from 'react';
import { GenericCrudCard, formatLocalDateTime } from 'common';
import { BadgeMovementData, badgeMovementsApi } from 'features/badgeMovement';
import {
	faArrowRight,
	faArrowLeft,
	faArrowRightArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface BadgeMovementsCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
}

// BadgeMovementsCrudCard component
export const BadgeMovementsCrudCard: React.FC<BadgeMovementsCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/badgeMovements',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "Numéro de badge"
		{
			label: 'Numéro de badge',
			field: 'badgeNumber',
			render: (a: BadgeMovementData) => a.badgeNumber,
		},
		// Colonne "Date, heure, sens" (sens = IN ou OUT selon direction) avec au début une flèche verte vers la droite ou rouge vers la gauche selon direction
		{
			label: 'Date, heure, sens',
			field: 'dateTime',
			filterType: 'date',
			render: (a: BadgeMovementData) => (
				<span>
					{a.direction === 'IN' ? (
						<FontAwesomeIcon
							icon={faArrowRight}
							style={{ color: 'green', marginRight: 4 }}
						/>
					) : a.direction === 'OUT' ? (
						<FontAwesomeIcon
							icon={faArrowLeft}
							style={{ color: 'red', marginRight: 4 }}
						/>
					) : null}
					{formatLocalDateTime(a.dateTime)}
				</span>
			),
			// Pour l'export : date seule (la direction est dans une colonne dédiée exportOnlyColumns)
			exportValue: (a: BadgeMovementData) => formatLocalDateTime(a.dateTime),
			// Permet de trier par date même si le rendu affiche aussi la direction
		},
		// Colonne "Nom et prénom du propriétaire"
		{
			label: 'Propriétaire',
			field: 'ownerLastName',
			render: (a: BadgeMovementData) =>
				`${a.ownerFirstName ?? ''} ${a.ownerLastName ?? ''}`.trim(),
		},
		// Colonne "Entreprise"
		{
			label: 'Entreprise',
			field: 'company',
			render: (a: BadgeMovementData) => a.company ?? '',
		},
		// Colonne "Affaire"
		{
			label: 'Affaire',
			field: 'project',
			render: (a: BadgeMovementData) => a.project ?? '',
		},
		// Colonne "Statut"
		{
			label: 'Statut',
			field: 'status',
			render: (a: BadgeMovementData) => a.status ?? '',
		},
		// Colone date d'import si extended
		...(extended
			? [
					{
						label: "Date d'import",
						field: 'importedAt',
						filterType: 'date',
						render: (a: BadgeMovementData) => formatLocalDateTime(a.importedAt),
					},
				]
			: []),
		// Colonne "Activité" si extended
		...(extended
			? [
					{
						label: 'Activité',
						field: 'activity.id',
						render: (a: BadgeMovementData) =>
							a.activity?.id ? 'ACT-' + a.activity.id : '',
						linkUrl: (a: BadgeMovementData) =>
							`/app/activities/${a.activity?.id}/edit`,
					},
				]
			: []),
	];

	return (
		<GenericCrudCard<BadgeMovementData>
			queryKey="badgeMovements"
			title={
				size === 'sm'
					? 'Mouvements de badges'
					: 'Liste des mouvements de badges'
			}
			fetchData={(token, options) =>
				badgeMovementsApi.listPaginated(token, {
					...options,
					sort: options?.sort || 'dateTime',
					order: options?.order || 'DESC',
				})
			}
			deleteItem={badgeMovementsApi.delete}
			tableColumns={columns}
			// Colonnes export uniquement : la direction est affichée visuellement dans le tableau
			// mais doit apparaître comme texte lisible dans le CSV
			exportOnlyColumns={[
				{
					label: 'Sens',
					exportValue: (a: BadgeMovementData) =>
						a.direction === 'IN'
							? 'Entrée'
							: a.direction === 'OUT'
								? 'Sortie'
								: '',
				},
			]}
			addButtonLabel="Ajouter un mouvement de badge"
			getAddPath={() => '/app/badgeMovements/new'}
			getEditPath={(id) => `/app/badgeMovements/${id}/edit`}
			emptyMessage="Aucun mouvement de badge trouvé."
			getReturnPath={hideReturn ? undefined : () => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faArrowRightArrowLeft : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};

export default BadgeMovementsCrudCard;
